
// Base.
import Component from 'vue-class-component'
import Vue from 'vue'
import { State } from 'vuex-class'

import {
  SchoolStaffNonNormalized,
  TeacherNonNormalized,
  SubstituteNonNormalized,
  StudentNonNormalized,
  User,
  UnknownNonNormalized,
} from '../models/user'
// Used to call service.
import UserService from '../services/UserService'

// Used Models.
import GridHeader from '../models/gridHeader'
import Permissions from '../models/constants/Permissions'

import RedButton from '@/components/Atomic/RedButton.vue'
import HelpTooltip from '@/components/Atomic/HelpTooltip.vue'
import CardTable from '@/components/Layout/CardTable.vue'

type UserType =
  | SchoolStaffNonNormalized
  | TeacherNonNormalized
  | SubstituteNonNormalized
  | StudentNonNormalized
  | User
  | UnknownNonNormalized

@Component({
  components: {
    CardTable,
    RedButton,
    HelpTooltip,
  },
})
export default class NonNormalizedAccounts extends Vue {
  @State
  private user!: any
  private users: UserType[] = []
  private filteredUsers: UserType[] = []
  private headers: GridHeader[] = []
  private isLoading: boolean = false
  private basicSearch: string = ''

  private selectedAccountType = ''

  private filters = new Set()
  private prenomFiltered = false
  private nomFiltered = false
  private identifiantFiltered = false
  private courrielFiltered = false
  private courriel_secondaireFiltered = false
  private courriel_autreFiltered = false
  private etablissement_idFiltered = false

  private accountType: string[] = []

  private mounted() {
    this.initialize()
  }

  private initialize() {
    this.addAccountType()
  }

  private addAccountType() {
    this.accountType = [
      'Enseignants',
      'Enseignants remplaçant',
      'Élèves',
      'Personnel administratif',
      'Type de compte inconnu',
    ]

    if (this.hasAccessToListWaitingForSyncAccounts) {
      this.accountType.push('Compte en attente de synchronisation')
    }
  }

  private async onSelectAccountType() {
    this.isLoading = true
    this.resetFilters()
    this.clearDataTable()
    switch (this.selectedAccountType) {
      case 'Personnel administratif':
        this.users = await UserService.getSchoolStaffNonNormalized()
        this.addHeadersForTeachersAndStaff()
        break
      case 'Enseignants':
        this.users = await UserService.getTeacherNonNormalized()
        this.addHeadersForTeachersAndStaff()
        break
      case 'Enseignants remplaçant':
        this.users = await UserService.getSubstituteNonNormalized()
        this.addHeadersForSubstitute()
        break
      case 'Élèves':
        this.users = await UserService.getStudentNonNormalized()
        this.addHeadersForStudents()
        break
      case 'Compte en attente de synchronisation':
        this.users = await UserService.getAccountsWaitingForSync()
        this.addHeadersForAccountsWaitingForSync()
        break
      case 'Type de compte inconnu':
        this.users = await UserService.getUnknownNotNormalized()
        this.addBaseHeaders()
        break

      default:
        this.users = []
        this.headers = []
    }
    this.filterLogical()
    this.isLoading = false
  }

  private addBaseHeaders() {
    this.headers.push(
      new GridHeader(
        this.$t('PersonneID').toString(),
        'personne_id',
        'left',
        true,
      ),
    )
    this.headers.push(
      new GridHeader(this.$t('Name').toString(), 'nom', 'left', true),
    )
    this.headers.push(
      new GridHeader(this.$t('FirstName').toString(), 'prenom', 'left', true),
    )
  }

  private addHeadersForStudents() {
    this.addBaseHeaders()
    this.headers.push(
      new GridHeader(
        this.$t('Establishment').toString(),
        'etablissement_id',
        'left',
        true,
      ),
    )
  }

  private addHeadersForSubstitute() {
    this.addBaseHeaders()
    this.headers.push(
      new GridHeader(
        this.$t('Username').toString(),
        'identifiant',
        'left',
        true,
      ),
    )
    this.headers.push(
      new GridHeader(this.$t('Email').toString(), 'courriel', 'left', true),
    )
  }

  private addHeadersForTeachersAndStaff() {
    this.addBaseHeaders()
    this.headers.push(
      new GridHeader(
        this.$t('Username').toString(),
        'identifiant',
        'left',
        true,
      ),
    )
    this.headers.push(
      new GridHeader(this.$t('Email').toString(), 'courriel', 'left', true),
    )
    this.headers.push(
      new GridHeader(
        this.$t('SecondaryEmail').toString(),
        'courriel_secondaire',
        'left',
        true,
      ),
    )
    this.headers.push(
      new GridHeader(
        this.$t('Establishment').toString(),
        'etablissement_id',
        'left',
        true,
      ),
    )
  }

  private addHeadersForAccountsWaitingForSync() {
    this.addBaseHeaders()
    this.headers.push(
      new GridHeader(
        this.$t('Username').toString(),
        'identifiant',
        'left',
        true,
      ),
    )
    this.headers.push(
      new GridHeader(this.$t('Email').toString(), 'courriel', 'left', true),
    )
    this.headers.push(
      new GridHeader(
        this.$t('SecondaryEmail').toString(),
        'courriel_autre',
        'left',
        true,
      ),
    )
  }

  private clearDataTable() {
    this.users = []
    this.filteredUsers = []
    this.headers = []
  }

  private resetFilters() {
    this.filters = new Set()
    this.prenomFiltered = false
    this.nomFiltered = false
    this.identifiantFiltered = false
    this.courrielFiltered = false
    this.courriel_secondaireFiltered = false
    this.courriel_autreFiltered = false
    this.etablissement_idFiltered = false
    this.filterLogical()
  }

  private getFilterItemName(filterName) {
    return this.headers.find( (header) => header.value === filterName)?.text
  }

  private resetFilter(filterValue) {
    const filterName = filterValue + 'Filtered'
    this.$data[filterName] = false
    this.updateFilters(filterValue)
  }

  private get hasAccessToListWaitingForSyncAccounts(): boolean {
    const allowed = [Permissions.ListWaitingForSyncAccounts]
    if (
      (this.user.permissions as []).some((permission) =>
        allowed.includes(permission),
      )
    ) {
      return true
    }
    return false
  }

  private get hasUsersToFilter() {
    return this.users.length > 0
  }

  private updateFilters(filterName: string) {
    if (this.filters.has(filterName)) {
      this.filters.delete(filterName)
    } else {
      this.filters.add(filterName)
    }
    this.filterLogical()
  }

  private filterLogical() {
    this.filteredUsers = this.users.filter((user) => {
      if (this.prenomFiltered && user.prenom !== '') {
        return
      }
      if (this.nomFiltered && user.nom !== '') {
        return
      }
      if (this.courrielFiltered && 'courriel' in user && user.courriel !== '') {
        return
      }
      if (
        this.identifiantFiltered &&
        'identifiant' in user &&
        user.identifiant !== ''
      ) {
        return
      }
      if (
        this.courriel_secondaireFiltered &&
        'courriel_secondaire' in user &&
        user.courriel_secondaire !== ''
      ) {
        return
      }
      if (
        this.courriel_autreFiltered &&
        'courriel_autre' in user &&
        user.courriel_autre !== ''
      ) {
        return
      }
      if (
        this.etablissement_idFiltered &&
        'etablissement_id' in user &&
        user.etablissement_id !== ''
      ) {
        return
      }
      return true
    })
  }
}
